/**
 * DECONECTAT PENTRU INACTIVITATE - semnalul dintre `client.ts` si bara.
 *
 * Serverul raspunde 401 cu `reason: 'idle'` cand jetonul a expirat de nelucru
 * (minutele furnizorului, Settings > General) dar e inca in orele lui: stie
 * cine e omul, deci intoarcerea e un cod pe mail in josul paginii, nu ecranul
 * de intrare cu tot ce era pe ecran pierdut. E `idle.js` din portal
 * (livrarea 26.9.10.1), fara jQuery: cine primeste un asemenea 401 anunta
 * aici, si `ui/IdleBar.tsx` asculta.
 *
 * O singura data: dupa primul semnal bara e deja pe ecran, iar celelalte
 * cereri picate in aceeasi clipa (TanStack porneste zece deodata) n-au ce
 * adauga.
 */
export const IDLE_EVENT = 'od:idle'

export type IdleReply = { reason?: string; minutes?: number }

/** E raspunsul asta un „ai stat prea mult", nu un „nu esti logat"? */
export function isIdleReply(body: unknown): body is IdleReply {
  return typeof body === 'object' && body !== null && (body as IdleReply).reason === 'idle'
}

let announced = false

/**
 * IESIREA VOITA NU E INACTIVITATE.
 *
 * Intre apasarea pe "Sign Out" si documentul de intrare trec cateva sute de
 * milisecunde, in care cererile deja pornite (TanStack, `session.ts`) se
 * intorc cu 401 - si, daca jetonul apucase sa expire de nelucru, cu
 * `reason: 'idle'`. Fara semnul asta, bara s-ar aprinde peste un om care
 * tocmai a cerut sa plece, in loc de ecranul de intrare.
 */
let leaving = false

/** Numai pentru probe. */
export function resetIdle(): void {
  announced = false
  leaving = false
}

/** Omul a apasat "Sign Out": de aici incolo niciun 401 nu mai e „ai stat prea mult". */
export function beginSignOut(): void {
  leaving = true
}

export function isSigningOut(): boolean {
  return leaving
}

export function announceIdle(minutes: number): void {
  if (announced || leaving) return
  announced = true
  window.dispatchEvent(new CustomEvent(IDLE_EVENT, { detail: { minutes } }))
}

/** Intoarce cu ce se opreste ascultarea. */
export function onIdle(listener: (minutes: number) => void): () => void {
  const handler = (event: Event): void => {
    listener((event as CustomEvent<{ minutes: number }>).detail?.minutes ?? 0)
  }
  window.addEventListener(IDLE_EVENT, handler)
  return () => window.removeEventListener(IDLE_EVENT, handler)
}

/**
 * Citeste corpul unui 401 fara sa consume raspunsul, si spune daca e de
 * inactivitate. Cine cheama hotaraste ce face cu restul.
 */
export async function idleMinutesOf(response: Response): Promise<number | null> {
  try {
    const body: unknown = await response.clone().json()
    if (isIdleReply(body)) return Number(body.minutes ?? 0)
  } catch {
    /* nu e JSON: nu e al nostru */
  }
  return null
}
